import Phaser from "phaser";
import type { TextDirection } from "@yjsgames/i18n";
import {
  parseInlineTokens,
  buildInlineWords,
  layoutInlineWords,
} from "./inlineText.js";

/**
 * 인라인 아이콘 1종의 렌더 사양 — 토큰 이름이 어떤 텍스처로 그려질지. 도메인 텍스처 키는
 * **호출자가 `resolveIcon`으로 주입**하므로 이 패키지는 게임 에셋 키를 모른다(DIP).
 */
export interface InlineIconSpec {
  readonly textureKey: string;
  /** 정사각 표시 크기(px). */
  readonly size: number;
  readonly tint?: number;
  /** 베이스라인 보정(px) — 아이콘이 글자보다 크거나 작을 때 세로 위치 미세조정. 기본 0. */
  readonly baselineNudge?: number;
}

export interface InlineIconTextOptions {
  /** 줄바꿈 기준 폭(px). */
  readonly maxWidth: number;
  /** 텍스트 조각 스타일(wordWrap은 무시 — 내부에서 자체 wrap). */
  readonly textStyle: Phaser.Types.GameObjects.Text.TextStyle;
  /** 줄별 정렬. 기본 "center". */
  readonly align?: "center" | "start";
  /** 텍스트 방향. 기본 "ltr". */
  readonly dir?: TextDirection;
  /** 텍스트 렌더 해상도 배율(고DPI 선명도). */
  readonly resolution?: number;
  /** 토큰 이름 → 아이콘 사양. undefined면 그 토큰은 단어에서 제외(레이아웃·렌더 모두 skip). */
  readonly resolveIcon: (name: string) => InlineIconSpec | undefined;
  /** 폭 측정 주입(테스트/커스텀 환경). 미주입 시 내부 2D canvas measureText 사용. */
  readonly measureText?: (text: string) => number;
}

/**
 * 텍스트 문장 흐름 중간에 `{icon:name}` 아이콘을 박는 리치 라벨(Phaser 뷰).
 * 레이아웃 계산은 `inlineText.ts`(순수)에 위임하고, 이 클래스는 측정·텍스처 렌더만 담당한다.
 *
 * Container는 origin을 지원하지 않으므로 콘텐츠를 `maxWidth × 전체높이` 박스 **중앙 기준**으로 평행이동해
 * `setOrigin(0.5)`을 에뮬레이트한다 — 호출자는 컨테이너를 원하는 중심점에 `setPosition`만 하면 된다.
 */
let sharedMeasureCtx: CanvasRenderingContext2D | null = null;
/** 폭 측정 전용 2D 컨텍스트(모듈 캐시) — Phaser Text.width가 미산출되는 환경(headless WebGL 등)을 우회. */
function getMeasureContext(): CanvasRenderingContext2D {
  if (!sharedMeasureCtx) {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("[InlineIconText] 2D canvas context unavailable");
    sharedMeasureCtx = ctx;
  }
  return sharedMeasureCtx;
}

export class InlineIconText extends Phaser.GameObjects.Container {
  private readonly opts: Required<Pick<InlineIconTextOptions, "maxWidth" | "textStyle" | "resolveIcon">> &
    Pick<InlineIconTextOptions, "align" | "dir" | "resolution" | "measureText">;
  private contentW = 0;
  private contentH = 0;

  constructor(scene: Phaser.Scene, x: number, y: number, text: string, options: InlineIconTextOptions) {
    super(scene, x, y);
    this.opts = options;
    this.rebuild(text);
    scene.add.existing(this);
  }

  /** 콘텐츠 폭(px) — 가장 넓은 줄. */
  get contentWidth(): number {
    return this.contentW;
  }

  /** 콘텐츠 높이(px) — 줄 수 × 줄 높이. 메시지 박스 높이 계산용(Phaser.Text.height 대체). */
  get contentHeight(): number {
    return this.contentH;
  }

  /** 텍스트 교체 + 재배치(언어 전환 in-place). */
  setText(text: string): this {
    this.rebuild(text);
    return this;
  }

  private rebuild(text: string): void {
    this.removeAll(true);
    const { maxWidth, textStyle, align, dir, resolution, resolveIcon } = this.opts;

    // 폭 측정 — Phaser Text.width는 일부 환경(headless WebGL 등)에서 미산출(undefined)되어 레이아웃이
    // 전부 겹친다. 2D canvas measureText로 직접 측정해(렌더러 무관·결정론) 회피한다. 줄 높이는 폰트 크기 근사.
    const fontSizePx = parseFloat(String(textStyle.fontSize ?? "16")) || 16;
    const fontFamily = String(textStyle.fontFamily ?? "sans-serif");
    let measureText: (s: string) => number;
    if (this.opts.measureText) {
      measureText = this.opts.measureText;
    } else {
      const mctx = getMeasureContext();
      mctx.font = `${fontSizePx}px ${fontFamily}`;
      measureText = (s: string): number => mctx.measureText(s).width;
    }
    const lineHeight = Math.ceil(fontSizePx * 1.35);
    // 미해결 아이콘은 null → buildInlineWords가 단어에서 제외(레이아웃·뷰 일치, 빈공간 방지).
    const measureIcon = (name: string): number | null => {
      const spec = resolveIcon(name);
      return spec ? spec.size : null;
    };

    const words = buildInlineWords(parseInlineTokens(text), measureText, measureIcon);
    const layout = layoutInlineWords(words, {
      maxWidth,
      lineHeight,
      align: align ?? "center",
      dir: dir ?? "ltr",
    });
    this.contentW = layout.maxLineWidth;
    this.contentH = layout.totalHeight;

    // maxWidth 박스 중앙 기준 평행이동(setOrigin(0.5) 에뮬레이트).
    const halfW = maxWidth / 2;
    const halfH = layout.totalHeight / 2;
    for (const p of layout.placements) {
      const cellY = p.y + lineHeight / 2 - halfH; // 줄 세로 중앙
      if (p.word.kind === "text") {
        const label = this.scene.add
          .text(p.x - halfW, cellY, p.word.text, textStyle)
          .setOrigin(0, 0.5);
        if (resolution) label.setResolution(resolution);
        this.add(label);
      } else {
        const spec = resolveIcon(p.word.name);
        if (!spec) continue;
        const img = this.scene.add
          .image(p.x - halfW + spec.size / 2, cellY + (spec.baselineNudge ?? 0), spec.textureKey)
          .setDisplaySize(spec.size, spec.size);
        if (spec.tint !== undefined) img.setTint(spec.tint);
        this.add(img);
      }
    }
  }
}
